import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MdArrowDropDown, MdSearch } from 'react-icons/md';

import { CAMADAS } from '../../../styles/camadas';

import { anelDeFoco, styledDs as styled } from '../../../styles/theme';

export type DsMultiSelectOption = {
  value: string;
  label: string;
};

export type DsMultiSelectProps = {
  label: string;
  options: DsMultiSelectOption[];
  values: string[];
  onApply: (values: string[]) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  maxChips?: number;
};

const CHIPS_VISIVEIS = 2;

const Root = styled.div`
  position: relative;
  font-family: ${({ theme }) => theme.font.family};
`;

const Trigger = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  height: 32px;
  min-width: 160px;
  max-width: 260px;
  padding: 0 8px 0 12px;
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-family: ${({ theme }) => theme.font.family};
  font-size: ${({ theme }) => theme.font.sizeMd};
  text-align: left;
  cursor: pointer;

  &:hover {
    border-color: ${({ theme }) => theme.colors.accent};
  }

  &:focus-visible {
    ${anelDeFoco('-1px')}
  }

  svg {
    width: 14px;
    height: 14px;
    flex: none;
    color: ${({ theme }) => theme.colors.textMuted};
  }
`;

const Resumo = styled.b`
  flex: 1;
  min-width: 0;
  color: ${({ theme }) => theme.colors.textPrimary};
  font-weight: ${({ theme }) => theme.weight.semibold};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Vazio = styled.span`
  flex: 1;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const Popover = styled.div`
  position: absolute;
  z-index: ${CAMADAS.listaDeFiltro};
  top: calc(100% + 6px);
  right: 0;
  min-width: 260px;
  max-width: 320px;
  border-radius: ${({ theme }) => theme.radius.lg};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.surface};
  box-shadow: ${({ theme }) => theme.shadow.card};
  overflow: hidden;
`;

const Busca = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  height: 34px;
  padding: 0 12px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};

  svg {
    width: 16px;
    height: 16px;
    flex: none;
    color: ${({ theme }) => theme.colors.textMuted};
  }

  input {
    flex: 1;
    min-width: 0;
    border: 0;
    outline: 0;
    background: transparent;
    color: ${({ theme }) => theme.colors.textPrimary};
    font-family: ${({ theme }) => theme.font.family};
    font-size: ${({ theme }) => theme.font.sizeMd};
  }
`;

const Lista = styled.div`
  max-height: 220px;
  overflow-y: auto;
  padding: 4px 0;
`;

const Linha = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 5px 12px;
  font-size: ${({ theme }) => theme.font.sizeMd};
  color: ${({ theme }) => theme.colors.textSecondary};
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.colors.accentSoft};
  }

  input {
    accent-color: ${({ theme }) => theme.colors.accent};
    width: 15px;
    height: 15px;
    flex: none;
  }
`;

const Rotulo = styled.span`
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Codigo = styled.span`
  flex: none;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: ${({ theme }) => theme.font.sizeXs};
`;

const LinhaTodas = styled(Linha)`
  padding: 7px 12px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  color: ${({ theme }) => theme.colors.textPrimary};
  font-weight: ${({ theme }) => theme.weight.semibold};
`;

const SemResultado = styled.p`
  margin: 0;
  padding: 10px 12px;
  font-size: ${({ theme }) => theme.font.sizeMd};
  color: ${({ theme }) => theme.colors.textMuted};
`;

const Rodape = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  font-size: ${({ theme }) => theme.font.sizeXs};
  color: ${({ theme }) => theme.colors.textMuted};
  white-space: nowrap;
`;

const Limpar = styled.button`
  margin-left: auto;
  border: 0;
  background: transparent;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-family: ${({ theme }) => theme.font.family};
  font-size: ${({ theme }) => theme.font.sizeMd};
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.colors.accent};
  }
`;

const Aplicar = styled.button`
  height: 28px;
  padding: 0 14px;
  border: 1px solid ${({ theme }) => theme.colors.accent};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.accent};
  color: ${({ theme }) => theme.colors.onAccent};
  font-family: ${({ theme }) => theme.font.family};
  font-size: ${({ theme }) => theme.font.sizeMd};
  font-weight: ${({ theme }) => theme.weight.semibold};
  cursor: pointer;

  &:hover {
    filter: brightness(1.08);
  }
`;

export function filtrarOpcoes(opcoes: DsMultiSelectOption[], termo: string): DsMultiSelectOption[] {
  const busca = termo.trim().toLowerCase();

  if (!busca) return opcoes;

  return opcoes.filter(
    (opcao) =>
      opcao.label.toLowerCase().includes(busca) || opcao.value.toLowerCase().includes(busca)
  );
}

export function resumirSelecao(
  selecionadas: DsMultiSelectOption[],
  total: number,
  limite: number
): string {
  if (selecionadas.length === total && total > 0) return 'Todas';

  const visiveis = selecionadas.slice(0, limite).map((opcao) => opcao.label);
  const restantes = selecionadas.length - visiveis.length;

  return restantes > 0 ? `${visiveis.join(', ')} +${restantes}` : visiveis.join(', ');
}

export function alternarValor(valores: string[], valor: string): string[] {
  return valores.includes(valor) ? valores.filter((atual) => atual !== valor) : [...valores, valor];
}

export const DsMultiSelect: React.FC<DsMultiSelectProps> = ({
  label,
  options,
  values,
  onApply,
  placeholder = 'Selecione',
  searchPlaceholder = 'Buscar...',
  maxChips = CHIPS_VISIVEIS,
}) => {
  const [aberto, setAberto] = useState(false);
  const [termo, setTermo] = useState('');
  const [rascunho, setRascunho] = useState<string[]>(values);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setRascunho(values);
  }, [values]);

  useEffect(() => {
    if (!aberto) return undefined;

    function aoClicarFora(evento: MouseEvent) {
      if (!rootRef.current?.contains(evento.target as Node)) setAberto(false);
    }

    document.addEventListener('mousedown', aoClicarFora);

    return () => document.removeEventListener('mousedown', aoClicarFora);
  }, [aberto]);

  const visiveis = useMemo(() => filtrarOpcoes(options, termo), [options, termo]);
  const selecionadas = useMemo(
    () => options.filter((opcao) => values.includes(opcao.value)),
    [options, values]
  );
  const todasMarcadas = options.length > 0 && rascunho.length === options.length;

  function abrir() {
    setRascunho(values);
    setTermo('');
    setAberto((atual) => !atual);
  }

  function aplicar(proximos: string[]) {
    setAberto(false);
    onApply(proximos);
  }

  return (
    <Root ref={rootRef}>
      <Trigger
        type="button"
        aria-label={label}
        aria-haspopup="listbox"
        aria-expanded={aberto}
        title={selecionadas.map((opcao) => opcao.label).join(', ')}
        onClick={abrir}
      >
        {selecionadas.length === 0 ? (
          <Vazio>{placeholder}</Vazio>
        ) : (
          <Resumo>{resumirSelecao(selecionadas, options.length, maxChips)}</Resumo>
        )}
        <MdArrowDropDown aria-hidden="true" />
      </Trigger>

      {aberto && (
        <Popover role="listbox" aria-label={`Opções de ${label}`}>
          <Busca>
            <MdSearch aria-hidden="true" />
            <input
              type="search"
              aria-label={`Buscar em ${label}`}
              placeholder={searchPlaceholder}
              value={termo}
              onChange={({ target }) => setTermo(target.value)}
            />
          </Busca>

          <LinhaTodas>
            <input
              type="checkbox"
              aria-label="Selecionar todas"
              checked={todasMarcadas}
              onChange={() => setRascunho(todasMarcadas ? [] : options.map((x) => x.value))}
            />
            <Rotulo>Selecionar todas</Rotulo>
          </LinhaTodas>

          <Lista>
            {visiveis.map((opcao) => (
              <Linha key={opcao.value}>
                <input
                  type="checkbox"
                  aria-label={opcao.label}
                  checked={rascunho.includes(opcao.value)}
                  onChange={() => setRascunho((atual) => alternarValor(atual, opcao.value))}
                />
                <Rotulo>{opcao.label}</Rotulo>
                {opcao.label !== opcao.value && <Codigo>{opcao.value}</Codigo>}
              </Linha>
            ))}
            {visiveis.length === 0 && <SemResultado>Nenhuma opção encontrada.</SemResultado>}
          </Lista>

          <Rodape>
            <span>
              {rascunho.length} de {options.length} selecionadas
            </span>
            <Limpar type="button" onClick={() => setRascunho([])}>
              Limpar
            </Limpar>
            <Aplicar type="button" onClick={() => aplicar(rascunho)}>
              Aplicar
            </Aplicar>
          </Rodape>
        </Popover>
      )}
    </Root>
  );
};
